import { useEffect } from "react";

import { useBBASveaAuthenticationGate } from "@bokio/bank/src/bba/useBBASveaAuthenticationGate";
import { FeedbackAnimation } from "@bokio/components/AnimatedFeedbackBlock/AnimatedFeedbackBlock";
import RenderRequest from "@bokio/components/RenderRequest/RenderRequest";
import { SignAndPayGateNotice } from "@bokio/components/SignAndPay/SignAndPayGateNotice/SignAndPayGateNotice";
import { SignAndPayLoginGate } from "@bokio/components/SignAndPay/SignAndPayLoginGate/SignAndPayLoginGate";
import {
	clearPaymentSigningProgressInfoFromLocalStorage,
	readPaymentSigningProgressInfoFromLocalStorage,
	savePaymentSigningProgressInfoToLocalStorage,
} from "@bokio/components/SveaBankIdAuthModal/sveaPollingUtils";
import { usePaymentContext } from "@bokio/contexts/PaymentContext/PaymentContext";
import { LoadingProgress } from "@bokio/designsystem/components/Loading";
import { Modal, ModalContent } from "@bokio/designsystem/components/Modal";
import { SuccessModal } from "@bokio/designsystem/components/SuccessModal/SuccessModal";
import { AnalyticsEventCategory, useMetric } from "@bokio/hooks/useMetric/useMetric";
import { useSignAndPay } from "@bokio/hooks/useSignAndPay/useSignAndPay";
import { GeneralLangFactory } from "@bokio/lang";
import BankLangFactory from "@bokio/lang/BankLangFactory";
import { useNewSveaAuthentication } from "@bokio/mobile-web-shared/areas/bank/hooks/useNewSveaAuthentication";
import { useCompanyUser } from "@bokio/shared/state/requests";

import { SignAndPayFooter } from "./SignAndPayFooter/SignAndPayFooter";

import type * as m from "@bokio/mobile-web-shared/core/model/model";
import type { FC } from "react";

import * as styles from "./signAndPayModal.scss";

type PaymentGroupForAuthorization = m.Bokio.Bank.Contract.Dtos.PaymentGroupForAuthorization;
type PaymentValidationRulesDto = m.Bokio.Bank.Contract.Dtos.PaymentValidationRulesDto;
interface SignAndPayModalInnerProps extends SignAndPayModalProps {
	paymentValidationRules: PaymentValidationRulesDto;
	showTransactionsAndPaymentsLinks?: boolean;
}

export const SignAndPayModalInner = ({
	initiallySelectedPaymentGroupIds,
	onSubmit,
	refreshPaymentCenter,
	visible,
	persistToast,
	onClose,
	paymentValidationRules,
	createPaymentAndexecuteAuthorizeEndpoint,
}: SignAndPayModalInnerProps) => {
	const generalLang = GeneralLangFactory();
	const bankLang = BankLangFactory();
	const permissions = useCompanyUser().companyUserPermissions;
	const { currentUserPermission } = usePaymentContext();
	const sveaAuth = useNewSveaAuthentication();
	const gateState = useBBASveaAuthenticationGate();
	const currentPsuInfo = gateState.type === "authenticated" ? gateState.currentPsuInfo : undefined;

	const [addMetric] = useMetric();

	const signAndPayState = useSignAndPay({
		paymentValidationRules,
		initiallySelectedPaymentGroupIds,
		persistToast: persistToast,
		currentPsuInfo,
		onSubmit: () => {
			addMetric(AnalyticsEventCategory.Bba, "SignedPayment", "FromSignAndPayModal");
			refreshPaymentCenter?.();
			onSubmit();
		},
		onAuthorizationFailed: refreshPaymentCenter,
		createPaymentAndexecuteAuthorizeEndpoint: createPaymentAndexecuteAuthorizeEndpoint,
	});

	useEffect(() => {
		if (visible) {
			signAndPayState.refreshPaymentListAndSummary();
		}
		const hasInprogressPaymentSigning = readPaymentSigningProgressInfoFromLocalStorage();

		// Directly start the authorize (BBA Migration)
		if (visible && createPaymentAndexecuteAuthorizeEndpoint) {
			signAndPayState.executeAuthorize();
		} else if (visible && hasInprogressPaymentSigning && hasInprogressPaymentSigning.paymentGroupIds) {
			signAndPayState.setSelectedPaymentGroupIds(hasInprogressPaymentSigning.paymentGroupIds);
			if (hasInprogressPaymentSigning.isPolling) {
				signAndPayState.executeAuthorize();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visible, createPaymentAndexecuteAuthorizeEndpoint]);

	// Feature toggle OFF: preserve existing behaviour exactly
	if (gateState.type === "disabled") {
		if (!gateState.canSignAndPay || permissions?.UploadBank === false) {
			return (
				<SuccessModal
					animation={FeedbackAnimation.Failure}
					onClose={onClose}
					visible={visible}
					title={bankLang.SignAndPayModalTitle}
					collapsible
					heading={generalLang.Forbidden}
				>
					{bankLang.SignAndPayModal_NoRights}
				</SuccessModal>
			);
		}
	}

	// Feature toggle ON: permission-based gating
	const isUnauthenticated = gateState.type === "unauthenticated";
	const isNoAccess = gateState.type === "notPermitted" || signAndPayState.restrictedAccessState === "noAccess";

	// Unauthenticated: gate replaces the entire footer (sign + view payments hidden)
	// No access: sign button hidden, view payments shown
	// Partial access: both buttons shown
	const hideSignButton = isUnauthenticated || isNoAccess;

	const forceDisableSign =
		gateState.type === "notPermitted" ||
		gateState.type === "unauthenticated" ||
		signAndPayState.restrictedAccessState === "noAccess" ||
		(gateState.type === "disabled" && (!currentUserPermission.CanSignPayment || permissions?.UploadBank === false));

	const renderModalTitle = () => {
		return (
			<>
				<div className={styles.heading}>{generalLang.BBA_PaymentsToSign}</div>
			</>
		);
	};

	const handleClose = () => {
		signAndPayState.stopPollingAndCancelSigning();
		clearPaymentSigningProgressInfoFromLocalStorage();
		onClose();
	};

	const handleOnSignAndPlayClick = () => {
		savePaymentSigningProgressInfoToLocalStorage({
			hasInitiated: true,
			isPolling: false,
			SuccessfulPaymentIds: undefined,
			paymentGroupIds: signAndPayState.selectedPaymentGroupIds,
			signingToken: undefined,
		});
		signAndPayState.executeAuthorize();
	};
	return (
		<Modal
			// Defer auto focus to make ModalBase properly find all the focusable elements
			autoFocus={!signAndPayState.paymentListRequest.isLoading}
			onClose={handleClose}
			visible={visible}
			title={renderModalTitle()}
			testId="Payments_SignAndPayModal"
		>
			<ModalContent className={styles.content}>{signAndPayState.renderSignAndPayContent(handleClose)}</ModalContent>
			<LoadingProgress request={signAndPayState.authorizeRequest} hideSoftErrors />
			<SignAndPayFooter
				disabled={signAndPayState.disableSign || forceDisableSign}
				onClose={handleClose}
				isLoading={signAndPayState.isAnyRequestLoading}
				onSignAndPayClick={handleOnSignAndPlayClick}
				multipleCurrencies={signAndPayState.multipleCurrencies}
				totalSum={signAndPayState.totalSum}
				selectedPayments={signAndPayState.selectedPaymentGroupIds.length}
				style="modal"
				hideSignButton={hideSignButton}
				noticeContent={
					<SignAndPayGateNotice
						gateType={gateState.type}
						restrictedAccessState={signAndPayState.restrictedAccessState}
					/>
				}
				gateContent={
					isUnauthenticated ? <SignAndPayLoginGate onAuthenticate={() => sveaAuth.authenticate()} /> : undefined
				}
			/>
		</Modal>
	);
};

export interface SignAndPayModalProps {
	visible: boolean;
	initiallySelectedPaymentGroupIds?: string[];
	persistToast?: boolean | undefined;
	onClose: () => void;
	onSubmit: () => void;
	refreshPaymentCenter?: () => void;
	createPaymentAndexecuteAuthorizeEndpoint?: (
		org: string,
		paymentGroups: PaymentGroupForAuthorization[],
	) => Promise<
		m.Envelope<m.Bokio.Bank.Contract.Dtos.SendAndAuthorizePaymentsResultDto, m.Bokio.Bank.Contract.Dtos.BankError>
	>;
}

export const SignAndPayModal: FC<SignAndPayModalProps> = props => {
	const { paymentValidationRulesRequest } = usePaymentContext();

	return (
		<RenderRequest request={paymentValidationRulesRequest} whenLoading={null}>
			{paymentValidationRules => <SignAndPayModalInner {...props} paymentValidationRules={paymentValidationRules} />}
		</RenderRequest>
	);
};
